import { Category, SavingsAccountMetadata } from '@square/dashboard-protos/models/squareup/banking/account_model';
import { AccountType } from '@square/dashboard-protos/models/squareup/banking/common';
import { WithholdingLevel } from './types';
import { getSvgNameForCategory } from '@banking/api/utils/bank-account';
import { Money } from '@square/dashboard-protos/models/squareup/connect/v2/common/money';
import { WithholdingConfig as BBFrontendWithholdingConfig } from '@square/dashboard-protos/models/squareup/bbfrontend/dashboardweb/withholding_config/withholding_config';
import { isMerchantWithholding, isUnitWithholding } from '@banking/api/utils/unified-account/types';
function emptyBalance(currency) {
  return Money.create({
    amount: 0,
    currency
  });
}
function getTotalBalance(account, currency) {
  return account.metadata?.savingsAccount?.totalBalance || account.availableBalance || emptyBalance(currency);
}
function getPendingBalance(account, currency) {
  return account.metadata?.savingsAccount?.pendingBalance || account.balanceAmounts?.pendingBalance || emptyBalance(currency);
}
function getMerchantLevelWithholdingBps(withholdingConfig, accountWithholdingConfig) {
  return withholdingConfig?.withholdingBps || accountWithholdingConfig?.withholdingConfigs.find(isMerchantWithholding)?.withholdingBps;
}
function getUnitWithholdingConfigsWithUnitNames({
  units,
  accountWithholdingConfig
}) {
  if (!units && !accountWithholdingConfig) {
    return undefined;
  }
  if (!units && accountWithholdingConfig) {
    throw new Error('Must provide units when accountWithholdingConfig is provided');
  }
  const unitWithholdingConfigs = accountWithholdingConfig?.withholdingConfigs?.filter(isUnitWithholding)?.map(withholdingConfig => {
    const unitWithholdingConfig = BBFrontendWithholdingConfig.create({
      ...withholdingConfig
    });
    unitWithholdingConfig.nickname = units?.find(unit => unit.token === withholdingConfig.owner?.token)?.nickname;
    return unitWithholdingConfig;
  }) || [];
  return unitWithholdingConfigs;
}
function getWithholdingLevel(accountWithholdingConfig, withholdingConfig) {
  if (!accountWithholdingConfig) {
    if (withholdingConfig) {
      return WithholdingLevel.MERCHANT;
    } else {
      return undefined;
    }
  }
  for (const withholdingConfig of accountWithholdingConfig.withholdingConfigs) {
    if (isMerchantWithholding(withholdingConfig) && withholdingConfig.isEnabled) {
      return WithholdingLevel.MERCHANT;
    } else if (isUnitWithholding(withholdingConfig) && withholdingConfig.isEnabled) {
      return WithholdingLevel.UNIT;
    }
  }
  return undefined;
}

// TODO(SFSP-2767): Update this function after we add effective_end_at field to the WithholdingConfig proto
// We should show the location names when a config is disabled too
function getLatestActiveWithholdingLocationNames(withholdingLevel, activeUnits, unitWithholdingConfigs) {
  if (!activeUnits || !withholdingLevel) {
    return undefined;
  }
  let locationNames = [];
  if (withholdingLevel === WithholdingLevel.MERCHANT) {
    locationNames = activeUnits.map(unit => unit.nickname);
  } else if (withholdingLevel === WithholdingLevel.UNIT && unitWithholdingConfigs) {
    locationNames = unitWithholdingConfigs.filter(config => config.isEnabled).map(config => config.nickname).filter(nickname => nickname !== undefined && nickname.length > 0);
  }
  return locationNames;
}
export function getMostRecentConfigs(configs) {
  let mostRecentConfigs = [];
  let mostRecent = null;
  for (const config of configs) {
    if (mostRecent) {
      if (config.effectiveEndAt) {
        if (mostRecent.instantUsec < config.effectiveEndAt.instantUsec) {
          mostRecentConfigs = [config];
          mostRecent = config.effectiveEndAt;
        } else if (mostRecent.instantUsec === config.effectiveEndAt.instantUsec) {
          mostRecentConfigs.push(config);
        }
      }
    } else if (config.effectiveEndAt) {
      mostRecentConfigs.push(config);
      mostRecent = config.effectiveEndAt;
    }
  }
  return mostRecentConfigs;
}
export function getUnitWithholdingBpsRange(units, unitWithholdingConfigs) {
  if (!units || !unitWithholdingConfigs) {
    return undefined;
  }
  const enabledConfigs = unitWithholdingConfigs.filter(config => config.isEnabled);
  const disabledConfigs = unitWithholdingConfigs.filter(config => !config.isEnabled);
  const enabledBpsList = enabledConfigs.map(config => config.withholdingBps).filter(Boolean);
  const merchantHasActiveUnitWithoutConfig = units.filter(u => u.unitActive).some(unit => !unitWithholdingConfigs.some(config => config.owner?.token === unit.token));
  if (enabledBpsList.length > 0) {
    return {
      maxUnitWithholdingBps: Math.max(...enabledBpsList),
      minUnitWithholdingBps: disabledConfigs.length > 0 || merchantHasActiveUnitWithoutConfig ? 0 : Math.min(...enabledBpsList)
    };
  } else {
    const mostRecentDisabledBpsList = getMostRecentConfigs(unitWithholdingConfigs).map(config => config.withholdingBps).filter(Boolean);
    return {
      maxUnitWithholdingBps: mostRecentDisabledBpsList.length > 0 ? Math.max(...mostRecentDisabledBpsList) : undefined,
      minUnitWithholdingBps: disabledConfigs.length > mostRecentDisabledBpsList.length || merchantHasActiveUnitWithoutConfig ? 0 : mostRecentDisabledBpsList.length > 0 ? Math.min(...mostRecentDisabledBpsList) : undefined
    };
  }
}
function hasMultipleEnabledWithholdingConfigTypes(accountWithholdingConfig) {
  if (!accountWithholdingConfig) {
    return false;
  }
  const enabledConfigTypes = new Set();
  for (const config of accountWithholdingConfig.withholdingConfigs) {
    // TODO(SFSP-2767): Owner should be a required field.
    if (config.isEnabled && config.owner) {
      enabledConfigTypes.add(config.owner.type);
    }
  }
  return enabledConfigTypes.size > 1;
}

// https://prototype.sqprod.co/#/docs/squareup.banking.Account
// https://prototype.sqprod.co/#/docs/squareup.banking.WithholdingConfig
export function fromAccount(account, {
  withholdingConfig,
  units,
  accountWithholdingConfig
} = {}) {
  if (!account.availableBalance) {
    throw new Error(`account ${account.id} has no available balance`);
  }
  if (![AccountType.SAVINGS_ACCOUNT, AccountType.FOLDERS_ACCOUNT].includes(account.type)) {
    throw new Error(`account ${account.id} is not a savings or folders account. it was type: ${account.type}`);
  }
  if (withholdingConfig && withholdingConfig.accountId !== account.id) {
    throw new Error(`withholdingConfig ${withholdingConfig.id} is not associated with account ${account.id}`);
  }
  if (hasMultipleEnabledWithholdingConfigTypes(accountWithholdingConfig)) {
    throw new Error('Multiple enabled config owner types have been found');
  }
  const currency = account.metadata?.savingsAccount?.totalBalance?.currency || account.availableBalance?.currency;

  // Never assume the currency, if we can't get it it's better to throw
  if (!currency) {
    throw new Error(`unable to get a currency associated with ${account.id}`);
  }
  const withholdingLevel = getWithholdingLevel(accountWithholdingConfig, withholdingConfig);
  const completedAt = withholdingConfig?.completedAt || accountWithholdingConfig?.completedAt && new Date(accountWithholdingConfig.completedAt.instantUsec / 1000);
  const svgModifierClass = completedAt ? 'banking-savings-account__icon-container--complete' : 'banking-savings-account__icon-container--default';
  const savingsCategory = account.metadata?.savingsAccount?.category;
  const foldersCategory = account.metadata?.foldersAccount?.category;
  const svgName = getSvgNameForCategory(savingsCategory || foldersCategory);
  const totalBalance = getTotalBalance(account, currency);
  const pendingBalance = getPendingBalance(account, currency);
  const totalAndPendingBalance = Money.create({
    amount: pendingBalance.amount + totalBalance.amount,
    currency
  });
  const merchantLevelWithholdingBps = getMerchantLevelWithholdingBps(withholdingConfig, accountWithholdingConfig);
  const unitWithholdingConfigs = getUnitWithholdingConfigsWithUnitNames({
    units,
    accountWithholdingConfig
  });
  const latestActiveWithholdingLocationNames = getLatestActiveWithholdingLocationNames(withholdingLevel, units, unitWithholdingConfigs);
  const withholdingEnabled = withholdingConfig?.enabled || accountWithholdingConfig?.withholdingConfigs.some(config => config.isEnabled) || false;
  const unitWithholdingBpsRange = getUnitWithholdingBpsRange(units, unitWithholdingConfigs);
  const unifiedAccount = {
    accountState: account.state,
    availableBalance: account.availableBalance,
    category: savingsCategory || foldersCategory,
    id: account.id,
    isPrimary: account.metadata?.savingsAccount?.isPrimary || false,
    isCustom: savingsCategory === SavingsAccountMetadata.Category.CUSTOM || foldersCategory === Category.CUSTOM,
    accountNumberSuffix: account.instruments?.bankAccount?.accountNumberSuffix,
    latestActiveWithholdingLocationNames,
    maxUnitWithholdingBps: unitWithholdingBpsRange?.maxUnitWithholdingBps,
    minUnitWithholdingBps: unitWithholdingBpsRange?.minUnitWithholdingBps,
    name: account.name,
    pendingBalance,
    svgName,
    svgModifierClass,
    targetAmount: withholdingConfig?.targetAmount || accountWithholdingConfig?.targetAmount,
    targetCompletedAt: completedAt ? new Date(completedAt) : undefined,
    totalAndPendingBalance,
    totalBalance,
    withholdingEnabled,
    withholdingLevel,
    withholdingBps: merchantLevelWithholdingBps,
    unitWithholdingConfigs
  };
  return unifiedAccount;
}